<template>
  <div class="layout-wrapper" :class="{ 'layout-sidebar-active': sidebarVisible }">
    <!-- Sidebar -->
    <div class="layout-sidebar">
      <div class="layout-sidebar-header">
        <h2 class="m-0">{{ t('app.title') }}</h2>
      </div>

      <div class="layout-menu">
        <router-link
          to="/"
          class="layout-menu-item"
          :class="{ active: $route.path === '/' }"
        >
          <font-awesome-icon icon="fa-solid fa-home" class="mr-2" />
          <span>{{ t('app.menu.dashboard') }}</span>
        </router-link>

        <router-link
          to="/employees"
          class="layout-menu-item"
          :class="{ active: $route.path.startsWith('/employees') }"
        >
          <font-awesome-icon icon="fa-solid fa-users" class="mr-2" />
          <span>{{ t('app.menu.employees') }}</span>
        </router-link>

        <template v-if="isAdmin">
          <router-link
            to="/services"
            class="layout-menu-item"
            :class="{ active: $route.path.startsWith('/services') }"
          >
            <font-awesome-icon icon="fa-solid fa-sitemap" class="mr-2" />
            <span>{{ t('app.menu.services') }}</span>
          </router-link>


        </template>
      </div>
    </div>

    <!-- Topbar -->
    <div class="layout-topbar">
      <div class="flex align-items-center">
        <Button 
          icon="fa-solid fa-bars" 
          @click="toggleSidebar" 
          class="p-button-text p-button-rounded layout-menu-button"
        />
      </div>

      <div class="layout-topbar-right">
        <LanguageSelector class="mr-3" />
        <span class="mr-3 hidden md:block">{{ user?.fullName }}</span>
        <Button 
          @click="logout" 
          class="p-button-text p-button-rounded"
          :tooltip="t('auth.logout')"
        >
          <template #icon>
            <font-awesome-icon icon="power-off" />
          </template>
        </Button>
      </div>
    </div>

    <!-- Main Content -->
    <div class="layout-main">
      <div class="layout-content">
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import LanguageSelector from './LanguageSelector.vue'

export default {
  components: {
    LanguageSelector
  },
  setup() {
    const store = useStore()
    const router = useRouter()
    const { t } = useI18n()
    const sidebarVisible = ref(true)

    const user = computed(() => store.state.auth.user)
    const isAdmin = computed(() => store.getters['auth/isAdmin'])

    const toggleSidebar = () => {
      sidebarVisible.value = !sidebarVisible.value
    }

    const logout = async () => {
      await store.dispatch('auth/logout')
      router.push('/login')
    }

    return {
      sidebarVisible,
      user,
      isAdmin,
      toggleSidebar,
      logout,
      t
    }
  }
}
</script>

<style>
.layout-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.layout-topbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 4rem;
  padding: 0 1.5rem;
  background-color: var(--surface-card);
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
  z-index: 999;
}

.layout-topbar-right {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.layout-sidebar {
  position: fixed;
  left: 0;
  top: 0;
  height: 100%;
  width: 250px;
  background: rgb(244,245,250);
  transition: transform .3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  box-shadow: 2px 0 8px rgba(0,0,0,0.1);
}

.layout-sidebar-header {
  height: 4rem;
  display: flex;
  align-items: center;
  padding: 0 1.5rem;
  border-bottom: 1px solid rgba(255,255,255,.08);
  color: #10b981;
  background-color: rgba(255,255,255);
}

.layout-menu {
  padding: 1.5rem 0;
  overflow-y: auto;
}

.layout-menu-item {
  display: flex;
  align-items: center;
  padding: 0.875rem 1.5rem;
  color: #64748b;
  text-decoration: none;
  transition: all .3s ease;
  border-radius: 6px;
  margin: 0 0.75rem 0.25rem 0.75rem;
}

.layout-menu-item:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.layout-menu-item.active {
  background-color: #10b981;
  color: #ffffff;
}

.layout-menu-item i {
  margin-right: 0.5rem;
  font-size: 1.25rem;
}

.layout-main {
  margin-left: 250px;
  margin-top: 4rem;
  min-height: calc(100vh - 4rem);
  padding: 2rem;
  transition: margin-left .3s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #f4f5fa;
}

@media screen and (max-width: 768px) {
  .layout-sidebar {
    transform: translateX(-100%);
  }

  .layout-sidebar-active .layout-sidebar {
    transform: translateX(0);
  }

  .layout-main {
    margin-left: 0;
  }
}
</style>
